import { Box, CardMedia, Typography, useTheme, Paper, Stack, IconButton, Divider, Dialog, DialogContent, DialogTitle } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ListMessage } from "@/utils/zod/messageSchema";
import { formatFileType } from "@/utils/formattingFunctions";
import { IProduct } from "@/utils/zod/productCollectionFormValidation";
import CloseIcon from '@mui/icons-material/Close';
import { useModal } from "@refinedev/core";
import palette from "@/palette";

export const ChatBubble = ({ message }: { message: ListMessage }) => {
  const theme = useTheme();
  const products = JSON.parse(message.products ?? "[]");

  const { show, visible, close } = useModal()

  return (
    <>
      <Box
        sx={{
          maxWidth: 400,
          width: "fit-content",
          minWidth: 250,
          alignSelf: "flex-end",
          backgroundColor: theme.palette.mode === 'dark' ? palette.dark.background.paper : palette.light.background.paper,
          borderRadius: 4,
          p: 2,
          paddingBlock: 1,
          mb: 1,
          ml: "auto",
          position: "relative",
        }}
      >
        {/* Sender name */}
        <Typography 
          variant="body1" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.mode === 'dark' ? palette.dark.text.primary : palette.light.text.primary,
            display: "block"
          }}
        >
          {message.account?.name}
        </Typography>
        
        {/* Message text */}
        {message.text && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.mode === 'dark' ? palette.dark.text.primary : palette.light.text.primary,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap'
            }}
          >
            {message.text}
          </Typography>
        )}

        {/* Media */}
        {message.cdn_url && (
          <Box sx={{
            paddingBlock: 1,
            maxWidth: 200,
            width: "fit-content",
            height: "fit-content",
          }}>
            <CardMedia
                component={formatFileType(message.format)}
                src={message.cdn_url}
                alt={message.format}
                sx={{ objectFit: 'cover', borderRadius: 4 }}
                controls
                controlsList="nodownload noremoteplayback nofullscreen"
            />
          </Box>
        )}

        {/* Products - Compact View (Chat Bubble) */}
        {message.products && products.length > 0 && (
          <Box 
            onClick={show}
            sx={{ 
              cursor: 'pointer',
              mt: 1,
              mb: 1,
              backgroundColor: theme.palette.mode === 'dark' ? palette.dark.background.paper : palette.light.background.paper,
              borderRadius: 2,
              overflow: "hidden",
              minWidth: 250,
              width: "fit-content",
              maxWidth: 350,
              height: "auto",
              border: `2px solid ${theme.palette.mode === 'dark' ? palette.dark.divider : palette.light.divider}`,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: theme.palette.mode === 'dark' ? palette.dark.grey[700] : palette.light.grey[100],
                color: theme.palette.mode === 'dark' ? palette.dark.text.primary : palette.light.text.primary,
                p: 1,
                borderBottom: `1px solid ${theme.palette.mode === 'dark' ? palette.dark.divider : palette.light.divider}`,
              }}
            >
              <Typography variant="body2" fontWeight="bold" color={theme.palette.mode === 'dark' ? palette.dark.text.primary : palette.light.text.primary}>
                {products.length} Products
              </Typography>
            </Paper>
            <Divider />
            
            <Grid container spacing={1} sx={{ p: 1, backgroundColor: theme.palette.mode === 'dark' ? palette.dark.grey[700] : palette.light.grey[100] }}>
              {products.map((product: IProduct) => (
                <Grid size={{ xs: 6 }} key={product.id} sx={{ height: 'auto', width: 'fit-content' }}>
                  <Box
                    sx={{
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                      width: 'fit-content',
                      height: 'fit-content',
                    }}
                  >
                    <CardMedia
                      component={formatFileType(product.medias[0].type)}
                      image={product.medias[0].cdn_url}
                      alt={product.name}
                      sx={{ 
                        height: 'auto',
                        width: '100px',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        
        {/* Timestamp for the whole message (only show if no products) */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.mode === 'dark' ? palette.dark.text.primary : palette.light.text.primary,
              display: "block",
              textAlign: "right",
            }}
          >
            {new Date(message.server_received_at).toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </Typography>
      </Box>

      {/* Products Modal */}
      <Dialog
        open={visible}
        onClose={close}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              maxHeight: '80vh'
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          pb: 1,
          backgroundColor: theme.palette.mode === 'dark' ? palette.dark.background.paper : palette.light.background.paper,
        }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            Products
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={close}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? palette.dark.grey[700] : palette.light.grey[100] }}>
          {products.map((product: IProduct) => (
            <Paper
              key={product.id}
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'white'
              }}
            >
              <Grid container sx={{ backgroundColor: theme.palette.mode === 'dark' ? palette.dark.background.paper : palette.light.background.paper }}>
                <Grid size={{ xs: 5 }}>
                  <CardMedia
                    component={formatFileType(product.medias[0].type)}
                    image={product.medias[0].cdn_url}
                    alt={product.name}
                    sx={{ 
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover'
                    }}
                    controls={product.medias[0].type === 'video' ? true : false}
                    controlsList="nodownload noremoteplayback nofullscreen"
                  />
                </Grid>
                <Grid size={{ xs: 7 }}>
                  <Stack direction="column" justifyContent="space-between" spacing={1.5} sx={{ p: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1, color: theme.palette.mode === 'dark' ? palette.dark.text.primary : palette.light.text.primary }}>
                      {product.name} <Typography component="span" variant="caption" color="text.secondary">({product.sku_id})</Typography>
                    </Typography>
                    
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color={"error"}>
                          ₹{Math.round(product.cost_price)}
                        </Typography>
                      </Stack>
                    </Stack>
                 
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }} >
                      <Stack direction="row" spacing={1} >
                        {product.minimum_order_quantity && (
                          <Typography variant="caption" color="text.secondary">
                            <Typography component="span" variant="body2" fontWeight="bold">MOQ:</Typography> {product.minimum_order_quantity}
                          </Typography>
                        )}

                      {product.uom && (
                        <Typography variant="caption" color="text.secondary">
                          <Typography component="span" variant="body2" fontWeight="bold">UOM:</Typography> {product.uom}
                        </Typography>
                        )}
                      </Stack>
                    </Stack>

                  <Stack direction="row" spacing={1} alignItems="start" justifyContent="space-between" sx={{ mt: 1 }}>
                    {product.material && (
                      <Typography variant="caption" color="text.secondary">
                        <Typography component="span" variant="body2" fontWeight="bold">Material:</Typography> {product.material}
                      </Typography>
                    )}
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                      {product.features && (
                        <Typography variant="caption" color="text.secondary">
                          <Typography component="span" variant="body2" fontWeight="bold">Features:</Typography> {product.features}
                        </Typography>
                      )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                  {product.description && (
                      <Typography variant="caption" color="text.secondary">
                        <Typography component="span" variant="body2" fontWeight="bold">Description:</Typography> {product.description}
                      </Typography>
                    )}
                  </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </DialogContent>
      </Dialog>
    </>
  );
};
